import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CompanyResponse, CurrentUserResponse } from '@demand-iq/user-sdk';
import { loginRedirectUrl } from 'clients/loginRedirect';
import { clearSpoofSession, companyIdStorageKey, mintSpoofToken } from 'clients/spoofSession';
import { queryClient } from 'contexts/QueryClient';
import { UserContext } from 'contexts/UserContext';
import useFeatures from 'hooks/data/features/useFeatures';
import useCompanyPlanUsage from 'hooks/data/workflows/useCompanyPlanUsage';
import useRouteTracker from 'hooks/useRouteTracker';
import useSpoofSessionRenewal from 'hooks/useSpoofSessionRenewal';
import { getUserToken, useToken } from 'hooks/useToken';
import { companyListing, loginRoute } from 'routes/routes';
import { toast } from 'sonner';
import { identifyUser } from 'utils/posthog';
import { useAuth } from './AuthProvider';

export function UserProvider({ children }: { children: JSX.Element }) {
  useRouteTracker();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const [user, setUser] = useState<CurrentUserResponse | null>(null);
  // DO NOT TRUST THIS HAS ALL CompanyResponse PROPS. Ticket: MDB-2100
  const [selectedCompany, setSelectedCompany] = useState<CompanyResponse | null>(null);

  const { removeTokenForClientId } = useToken();

  const {
    clients: { user: userClient, company: companyClient },
  } = useAuth();

  const { data: availableFeatures } = useFeatures(selectedCompany?.id);
  const end = useMemo(() => new Date(), []);
  const start = useMemo(() => {
    return new Date(end.getFullYear(), end.getMonth(), 1);
  }, [end]);

  const { data: planUsage } = useCompanyPlanUsage({
    startDate: start,
    endDate: end,
    companyId: selectedCompany?.id,
  });

  // Mints the spoof token. Storage is owned by clients/spoofSession so the same mint path
  // serves the initial switch, the scheduled renewal, and the 401 recovery in authUtils.
  const spoofInto = useCallback(async (companyId: string): Promise<void> => {
    const token = await mintSpoofToken(companyId);
    if (!token) {
      throw new Error('Could not switch into company');
    }
  }, []);

  useEffect(() => {
    const handleUser = async (firstCall: boolean) => {
      const userToken = getUserToken(user?.username ?? localStorage.getItem('user')?.toLowerCase());
      // Check if token is core and is not the activate page since we don't need it.
      if (userToken && !document.location.pathname.includes('/newPassword')) {
        try {
          const response = await userClient.getCurrentUser();
          const lastCompany = sessionStorage.getItem(companyIdStorageKey);
          // Either no company prior selected or last company is the user company.
          if (lastCompany === null || lastCompany === response?.company?.id) {
            setSelectedCompany(response?.company ?? null);
          } else {
            const companyResponse = await companyClient.getCompany({ id: lastCompany });
            setSelectedCompany(companyResponse);
          }
          setUser(response);
          identifyUser({
            id: response.id,
            email: response.username,
            companyId: response.company?.id,
            companyName: response.company?.companyName,
            role: response.roleName,
            username: response.username,
          });
          queryClient.invalidateQueries();
        } catch (_e) {
          // Don't like this approach but we need to have the tokens refresh and try again.
          if (firstCall) {
            await handleUser(false);
          } else {
            removeTokenForClientId('core', 'user');
            if (window.location.pathname !== loginRoute) {
              window.location.href = loginRedirectUrl(window.location.pathname, window.location.search);
            }
          }
        }
      } else if (
        !document.location.pathname.includes(loginRoute) &&
        !document.location.pathname.includes('newPassword') &&
        !document.location.pathname.includes('resetPassword')
      ) {
        window.location.href = loginRedirectUrl(window.location.pathname, window.location.search);
      }
    };
    handleUser(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveAndSetCompany = useCallback(
    (company: CompanyResponse | null, navigateUrl?: string) => {
      queryClient.clear();
      if (company && user?.company?.id !== company.id) {
        spoofInto(company.id)
          .then(() => {
            setSelectedCompany(company);
            if (navigateUrl) {
              navigate(navigateUrl);
            }
          })
          .catch(() => {
            toast.error(t('spoof-switch-failed', 'Could not open that company. Please try again.'));
          });
      } else {
        clearSpoofSession();
        setSelectedCompany(company);
        if (navigateUrl) {
          navigate(navigateUrl);
        }
      }
    },
    [user?.company?.id, spoofInto, navigate, t]
  );

  // Spoof state is never persisted, only derived from the selected company diverging from
  // the user's own. Exposed on the context so `useSpoofedCompanyId` and `CompanyBanner`
  // share this one derivation.
  const spoofedCompanyId =
    user && selectedCompany && selectedCompany.id !== user.company?.id ? selectedCompany.id : null;

  const handleSpoofSessionEnd = useCallback(() => {
    toast.warning(
      t('spoof-session-ended', 'Your session for that company ended. Open it again to keep working in it.')
    );
    saveAndSetCompany(null, companyListing);
  }, [saveAndSetCompany, t]);

  useSpoofSessionRenewal({ companyId: spoofedCompanyId, onSessionEnd: handleSpoofSessionEnd });

  const updateCompanyData = useCallback((company: CompanyResponse) => {
    setSelectedCompany(company);
  }, []);

  const value = useMemo(() => {
    const modifiedUser = user;
    if (selectedCompany == null) {
      setSelectedCompany(user?.company ?? null);
    }
    // removing company since we are using the selected state instead. This enables the spoofing if user is allowed
    return {
      user: modifiedUser,
      company: selectedCompany,
      setCompany: saveAndSetCompany,
      updateCompanyData,
      spoofedCompanyId,
      availableFeatures: availableFeatures ?? null,
      planUsage: planUsage ?? null,
    };
  }, [user, selectedCompany, saveAndSetCompany, updateCompanyData, spoofedCompanyId, availableFeatures, planUsage]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const useUserInfo = () => useContext(UserContext);
